/* eslint-disable react/prop-types */
import React, { useRef, useState, useCallback } from "react";
import { Line } from "react-chartjs-2";
import CustomTooltip from "app/common/chart/CustomTooltipComponent";
import { Typography, useMediaQuery } from "@material-ui/core";
import { nFormatter } from "lib/utils";
import _ from "lodash";

function PriceTimeSeriesChartComponent({ data }) {
  const refs = useRef();
  const [tooltipProps, setTooltipProps] = useState({});
  const [selectedDatasetIndex, setSelectedDatasetIndex] = useState(null);
  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down("md"));

  const setPositionAndData = (payload) => {
    setTooltipProps({
      top: payload.top,
      left: payload.left,
      position: "fixed",
      tooltipModel: payload.tooltipModel,
      showTooltip: true,
    });
  };

  const hide = () => {
    setTooltipProps({
      ...tooltipProps,
      showTooltip: false,
    });
  };

  const chartOptions = {
    scales: {
      xAxes: [
        {
          id: "Time",
          gridLines: {
            drawOnChartArea: false,
            drawTicks: false,
          },
          ticks: {
            padding: 10,
            beginAtZero: false,
            autoSkip: true,
            maxTicksLimit: 6,
            maxRotation: isSmallScreen ? 45 : 0,
          },
        },
      ],
      yAxes: [
        {
          id: data.axisName,
          scaleLabel: {
            labelString: data.axisName,
            display: false,
          },
          gridLines: {
            drawOnChartArea: true,
            drawTicks: false,
          },
          ticks: {
            padding: 10,
            beginAtZero: false,
            autoSkip: true,
            maxTicksLimit: 6,
            maxRotation: 0,
            minRotation: 0,
            callback: React.useCallback((value) => {
              return `${nFormatter(value)}`;
            }, []),
          },
          position: "left",
        },
      ],
    },
    layout: {
      padding: {
        left: 10,
        right: 15,
        top: 15,
        bottom: 15,
      },
    },
    legend: {
      display: false,
    },
    hover: {
      mode: "nearest",
      axis: "x",
      intersect: isSmallScreen,
    },
    onHover: useCallback((event, chartElement) => {
      if (chartElement[0]) {
        // eslint-disable-next-line no-underscore-dangle
        setSelectedDatasetIndex(chartElement[0]._datasetIndex);
      }
    }, []),
    tooltips: {
      enabled: false,
      intersect: isSmallScreen,
      mode: "nearest",
      axis: "x",
      custom: React.useCallback((tooltipModel) => {
        if (tooltipModel.opacity === 0) {
          hide();
          return;
        }

        const chart = refs.current.chartInstance;
        const { canvas } = chart.ctx;

        if (canvas) {
          const position = canvas.getBoundingClientRect();

          // set position of tooltip
          const left =
            tooltipModel.caretX <= chart.width / 2
              ? position.left + tooltipModel.caretX + 40
              : position.left + tooltipModel.caretX - 220;
          const top =
            tooltipModel.caretY <= chart.height / 2
              ? position.top + tooltipModel.caretY - 20
              : position.top + tooltipModel.caretY - 80;

          setPositionAndData({ top, left, tooltipModel });
        }
      }, []),
    },
    responsive: true,
    maintainAspectRatio: false,
  };

  return (
    <>
      {tooltipProps.showTooltip ? (
        <CustomTooltip
          tooltipProps={tooltipProps}
          items={_.map(tooltipProps.tooltipModel.dataPoints, (dataPoint) => {
            if (
              (tooltipProps.tooltipModel.dataPoints.length > 5 &&
                dataPoint.datasetIndex !== selectedDatasetIndex) ||
              refs.current.chartInstance.data.datasets[dataPoint.datasetIndex]
                .data[dataPoint.index]?.ignore
            )
              return null;
            return {
              content: (
                <div>
                  <Typography variant="body2">
                    {
                      refs.current.chartInstance.data.datasets[
                        dataPoint.datasetIndex
                      ].title
                    }
                  </Typography>
                  <Typography variant="caption">
                    {nFormatter(parseFloat(dataPoint.value).toFixed(2))}
                  </Typography>
                </div>
              ),
              borderStyle: {
                border: refs.current.chartInstance.data.datasets[
                  dataPoint.datasetIndex
                ]?.borderDash?.length
                  ? `dashed 2px ${
                      refs.current.chartInstance.data.datasets[
                        dataPoint.datasetIndex
                      ].hoverBorderColor
                    }`
                  : `2px solid ${
                      refs.current.chartInstance.data.datasets[
                        dataPoint.datasetIndex
                      ].borderColor
                    }`,
              },
            };
          })}
          tooltipData={{
            title: `${data.title} - ${
              refs.current.chartInstance.data.datasets[
                tooltipProps.tooltipModel.dataPoints[0].datasetIndex
              ].data[tooltipProps.tooltipModel.dataPoints[0].index].x
            }`,
          }}
        />
      ) : null}
      <Line ref={refs} data={data} options={chartOptions} />
    </>
  );
}

export default PriceTimeSeriesChartComponent;
